<template>
  <main class="container">
    <section class="hero">
      <div>
        <h1>Animaux à l’adoption</h1>
        <p>
          Parcourez les animaux disponibles dans nos refuges partenaires. Filtrez par espèce,
          localisation et critères pour trouver le compagnon qui vous correspond.
        </p>
      </div>
    </section>

    <section class="toolbar">
      <div class="search" role="search">
        <span class="icon" aria-hidden="true"></span>
        <input
          v-model="query"
          type="text"
          placeholder="Rechercher un animal, une ville, un refuge…"
        />
      </div>
    </section>

    <div class="resultsBar">
      <div>
        Affichage : <strong>{{ filteredAnimals.length }}</strong> résultat{{ filteredAnimals.length > 1 ? "s" : "" }}
      </div>
      <div class="sort">
        <span>Trier par</span>
        <select v-model="sortBy">
          <option value="recent">Les plus récents</option>
          <option value="age_asc">Âge croissant</option>
          <option value="age_desc">Âge décroissant</option>
        </select>
      </div>
    </div>

    <p v-if="loading" style="margin: 14px 0; font-weight: 800;">
      Chargement des animaux...
    </p>

    <p v-else-if="error" style="margin: 14px 0; font-weight: 800; color:#b91c1c;">
      {{ error }}
    </p>

    <section v-else class="grid">
      <article v-for="a in paginatedAnimals" :key="a.id" class="card">
        <div class="photo" :style="photoStyle(a)">
          <span class="tag">{{ capitalize(a.type) }}</span>
          <button class="fav" aria-label="Favori"></button>
        </div>

        <div class="content">
          <div class="nameRow">
            <div class="name">{{ a.name }}</div>
          </div>

          <div class="sub">
            {{ formatAgeFromBirth(a.naissance) }} • {{ sexLabel(a.sexe) }} • {{ a.name }}
          </div>

          <div class="meta">
          </div>

          <div class="actions">
            <!-- si tu as une route Vue Router: <RouterLink ...> -->
            <a class="link primary" :href="`/animal/${a.id}`">Voir la fiche</a>
            <a class="link" :href="a.website_url" target="_blank" rel="noopener noreferrer">
  Contacter
</a>
          </div>
        </div>
      </article>

      <p v-if="filteredAnimals.length === 0" style="margin: 14px 0; color:#6b7280; font-weight:700;">
        Aucun animal ne correspond à votre recherche.
      </p>
    </section>

    <div v-if="!loading && !error && filteredAnimals.length" class="pager" aria-label="Pagination">
      <button type="button" @click="prevPage" :disabled="page === 1">Précédent</button>
      <button type="button" @click="nextPage" :disabled="page >= totalPages">Suivant</button>
    </div>
  </main>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";

const API_URL = "https://admin.asso-refuge.fr/getAllAnimal";


const IMG_BASE_URL = "https://admin.asso-refuge.fr/images/";

const animals = ref([]);
const loading = ref(true);
const error = ref(null);

const query = ref("");
const sortBy = ref("recent");

// pagination
const page = ref(1);
const pageSize = 24;

async function fetchAnimals() {
  loading.value = true;
  error.value = null;

  try {
    const res = await fetch(API_URL, {
      method: "GET",
      headers: { Accept: "application/json" },
    });

    if (!res.ok) throw new Error(`HTTP ${res.status} - ${res.statusText}`);

    const data = await res.json();
    // ton endpoint renvoie visiblement un array direct
    animals.value = Array.isArray(data) ? data : (data?.data ?? []);
  } catch (e) {
    error.value = e?.message ?? String(e);
  } finally {
    loading.value = false;
  }
}

onMounted(fetchAnimals);

watch([query, sortBy], () => {
  page.value = 1;
});

function capitalize(s) {
  if (!s) return "Animal";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function sexLabel(sexe) {
  const raw = (sexe ?? "").toLowerCase();
  if (raw === "male" || raw === "m" || raw === "mâle") return "Mâle";
  if (raw === "female" || raw === "f" || raw === "femelle") return "Femelle";
  return "—";
}

/**
 * naissance: "2018-07-15"
 * => "7 ans" / "8 mois"
 */
function formatAgeFromBirth(naissance) {
  if (!naissance) return "Âge —";
  const birth = new Date(naissance);
  if (Number.isNaN(birth.getTime())) return "Âge —";

  const now = new Date();
  let months = (now.getFullYear() - birth.getFullYear()) * 12 + (now.getMonth() - birth.getMonth());

  // si le jour du mois n’est pas encore passé, on retire 1 mois
  if (now.getDate() < birth.getDate()) months -= 1;

  if (months < 0) months = 0;

  if (months >= 12) {
    const years = Math.floor(months / 12);
    return `${years} an${years > 1 ? "s" : ""}`;
  }
  return `${months} mois`;
}

function photoStyle(a) {
  // on prend img1 sinon img2/img3
  const file = a?.img1 ?? a?.img2 ?? a?.img3;
  if (file) {
    const url = `${IMG_BASE_URL}${file}`;
    return {
      backgroundImage: `url("${url}")`,
      backgroundSize: "cover",
      backgroundPosition: "center",
    };
  }
  return { background: "#e5e7eb" };
}

function parseAgeMonthsFromBirth(naissance) {
  if (!naissance) return null;
  const birth = new Date(naissance);
  if (Number.isNaN(birth.getTime())) return null;

  const now = new Date();
  let months = (now.getFullYear() - birth.getFullYear()) * 12 + (now.getMonth() - birth.getMonth());
  if (now.getDate() < birth.getDate()) months -= 1;
  return Math.max(0, months);
}

const filteredAnimals = computed(() => {
  const q = query.value.trim().toLowerCase();
  let list = animals.value.slice();

  // search
  if (q) {
    list = list.filter((a) => {
      const hay = [
        a.name,
        a.type,
        a.ville,
        a.race,
        a.id_refuge ? `refuge ${a.id_refuge}` : "",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return hay.includes(q);
    });
  }

  // sort
  if (sortBy.value === "age_asc" || sortBy.value === "age_desc") {
    list.sort((a, b) => {
      const am = parseAgeMonthsFromBirth(a.naissance);
      const bm = parseAgeMonthsFromBirth(b.naissance);

      const aVal = am == null ? Number.POSITIVE_INFINITY : am;
      const bVal = bm == null ? Number.POSITIVE_INFINITY : bm;

      return sortBy.value === "age_asc" ? aVal - bVal : bVal - aVal;
    });
  } else {
    // recent
    list.sort((a, b) => {
      const ad = new Date(a?.created_at ?? 0).getTime() || 0;
      const bd = new Date(b?.created_at ?? 0).getTime() || 0;
      return bd - ad;
    });
  }

  return list;
});

const totalPages = computed(() => Math.max(1, Math.ceil(filteredAnimals.value.length / pageSize)));

const paginatedAnimals = computed(() => {
  const start = (page.value - 1) * pageSize;
  return filteredAnimals.value.slice(start, start + pageSize);
});

function scrollToTop() {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
}

function nextPage() {
  if (page.value < totalPages.value) {
    page.value += 1;
    scrollToTop();
  }
}

function prevPage() {
  if (page.value > 1) {
    page.value -= 1;
    scrollToTop();
  }
}
</script>

<style src="../assets/css/animaux.css" scoped></style>
